// Activity Feed — live merged stream of tag_events + location_events.
// Matches the existing list-screen recipe (PersonnelRoster, RescueChambers,
// Locations): toolbar slot → SummaryCards → bordered surfaceCard container
// with a uppercase column header row + scrollable hover-highlighted rows.
// Auto-prepends as realtime INSERT events fire.

import React, {useMemo} from 'react'
import clsx from 'clsx'
import {ArrowDownToLine, ArrowUpFromLine, MapPin} from 'lucide-react'
import {Avatar} from '../components/common/Avatar'
import {SummaryCard} from '../components/common/SummaryCard'
import {useSupabaseSubscription} from '../app/useSupabaseSubscription'
import {formatTimeAgo} from '../lib/helpers'

interface TagEvent {
  id: string
  tag_id: string
  personnel_id: string | null
  equipment_id: string | null
  type: 'IN' | 'OUT'
  timestamp: string
}

interface LocationEvent {
  id: string
  location_id: string
  personnel_id: string
  event_type: 'entry_in' | 'entry_out'
  timestamp: string
}

interface PersonnelRow {
  id: string
  name: string
  photo: string | null
}

interface LocationLite {
  id: string
  name: string
}

interface FeedItem {
  id: string
  timestamp: string
  kind: 'tag' | 'location'
  action: 'IN' | 'OUT'
  primaryLabel: string  // who
  secondaryLabel: string // where / what tag
  photo: string | null
}

export const ActivityFeed: React.FC = () => {
  const {rows: tagEvents, loading: teL, error: teE} =
    useSupabaseSubscription<TagEvent>({
      table: 'tag_events',
      columns: 'id, tag_id, personnel_id, equipment_id, type, timestamp',
      orderBy: ['timestamp', false],
      scopedToMine: false,
      events: ['INSERT'],
    })

  const {rows: locEvents, loading: leL, error: leE} =
    useSupabaseSubscription<LocationEvent>({
      table: 'location_events',
      columns: 'id, location_id, personnel_id, event_type, timestamp',
      orderBy: ['timestamp', false],
      scopedToMine: false,
      events: ['INSERT'],
    })

  const {rows: personnel} = useSupabaseSubscription<PersonnelRow>({
    table: 'personnel',
    columns: 'id, name, photo',
    events: ['UPDATE'],
  })

  const {rows: locations} = useSupabaseSubscription<LocationLite>({
    table: 'locations',
    columns: 'id, name',
  })

  const personById = useMemo(() => {
    const m = new Map<string, PersonnelRow>()
    for (const p of personnel) m.set(p.id, p)
    return m
  }, [personnel])

  const locById = useMemo(() => {
    const m = new Map<string, LocationLite>()
    for (const l of locations) m.set(l.id, l)
    return m
  }, [locations])

  const feed = useMemo<FeedItem[]>(() => {
    const items: FeedItem[] = []
    for (const e of tagEvents) {
      const person = e.personnel_id ? personById.get(e.personnel_id) : null
      items.push({
        id: e.id,
        timestamp: e.timestamp,
        kind: 'tag',
        action: e.type,
        primaryLabel: person?.name ?? (e.equipment_id ? 'Equipment' : 'Unknown'),
        secondaryLabel: `Tag #${e.tag_id}`,
        photo: person?.photo ?? null,
      })
    }
    for (const e of locEvents) {
      const person = personById.get(e.personnel_id)
      const loc = locById.get(e.location_id)
      items.push({
        id: e.id,
        timestamp: e.timestamp,
        kind: 'location',
        action: e.event_type === 'entry_in' ? 'IN' : 'OUT',
        primaryLabel: person?.name ?? 'Unknown',
        secondaryLabel: loc?.name ?? 'Unknown location',
        photo: person?.photo ?? null,
      })
    }
    items.sort((a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp))
    return items.slice(0, 200)
  }, [tagEvents, locEvents, personById, locById])

  const summary = useMemo(() => {
    let tagIns = 0
    let tagOuts = 0
    let zoneIns = 0
    let zoneOuts = 0
    for (const it of feed) {
      if (it.kind === 'tag') {
        if (it.action === 'IN') tagIns++
        else tagOuts++
      } else {
        if (it.action === 'IN') zoneIns++
        else zoneOuts++
      }
    }
    return {tagIns, tagOuts, zoneIns, zoneOuts}
  }, [feed])

  if (teL || leL) {
    return (
      <div className="flex h-full items-center justify-center text-textMuted">
        Loading activity…
      </div>
    )
  }
  if (teE || leE) {
    return (
      <div className="flex h-full items-center justify-center p-xxxl">
        <div className="rounded-lg border border-statusRed bg-surfaceCard p-xxl">
          <h3 className="mb-md text-lg font-semibold text-statusRed">Fetch failed</h3>
          <pre className="whitespace-pre-wrap font-mono text-sm text-textPrimary">
            {teE || leE}
          </pre>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      {/* Summary cards — match the PersonnelRoster / Locations pattern:
          horizontal flex, gap-md, mb-lg, accent stripe on the left edge. */}
      <div className="mb-lg mt-md flex gap-md px-xl">
        <SummaryCard
          label="Tag-Ins"
          value={summary.tagIns}
          valueClass="text-statusGreen"
          accentColor="var(--color-statusGreen)"
        />
        <SummaryCard
          label="Tag-Outs"
          value={summary.tagOuts}
          valueClass="text-statusRed"
          accentColor="var(--color-statusRed)"
        />
        <SummaryCard
          label="Zone Entries"
          value={summary.zoneIns}
          accentColor="var(--color-primary)"
        />
        <SummaryCard
          label="Zone Exits"
          value={summary.zoneOuts}
          accentColor="var(--color-textMuted)"
        />
      </div>

      {/* Feed container — same recipe as Personnel/Locations: rounded-lg
          border surfaceBorder bg-surfaceCard, uppercase column header in
          surface bg, scrollable body with divider rows and hover state. */}
      <div className="mx-xl mb-xl flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-surfaceBorder bg-surfaceCard">
        <div className="grid shrink-0 grid-cols-[44px_1fr_auto] items-center gap-md border-b border-divider bg-surface px-lg py-md text-xs font-semibold uppercase text-textMuted">
          <div />
          <div>Event · {feed.length}</div>
          <div className="text-right">When</div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {feed.length === 0 ? (
            <div className="flex h-full items-center justify-center text-textMuted">
              No activity yet. Tag events and location entries will appear here in real time.
            </div>
          ) : (
            feed.map(item => <FeedRow key={`${item.kind}-${item.id}`} item={item} />)
          )}
        </div>
      </div>
    </div>
  )
}

const FeedRow: React.FC<{item: FeedItem}> = ({item}) => {
  const isIn = item.action === 'IN'
  const ActionIcon = item.kind === 'location' ? MapPin : isIn ? ArrowDownToLine : ArrowUpFromLine
  const actionColor = isIn ? 'var(--color-statusGreen)' : 'var(--color-statusRed)'
  const actionLabel =
    item.kind === 'location'
      ? isIn ? 'Entered zone' : 'Exited zone'
      : isIn ? 'Tagged In' : 'Tagged Out'

  return (
    <div
      className={clsx(
        'grid grid-cols-[44px_1fr_auto] items-center gap-md px-lg py-md text-left',
        'border-b border-divider hover:bg-surfaceLight/40',
      )}
    >
      <Avatar uri={item.photo} name={item.primaryLabel} size={36} />
      <div className="min-w-0">
        <div className="truncate font-semibold text-textPrimary">
          {item.primaryLabel}
        </div>
        <div className="flex items-center gap-xs text-sm text-textSecondary">
          <ActionIcon size={12} color={actionColor} />
          <span style={{color: actionColor}}>{actionLabel}</span>
          <span className="text-textMuted">· {item.secondaryLabel}</span>
        </div>
      </div>
      <span className="shrink-0 text-xs text-textMuted">{formatTimeAgo(item.timestamp)}</span>
    </div>
  )
}
